import { AnyEventObject, assign, createMachine } from 'xstate';
import { pipe } from 'fp-ts/function';
import { ApolloError, MutationFunctionOptions, FetchResult } from '@apollo/client';
import { rejectGraphqlError } from 'modules/ai/utils';
import {
  createGoalPreviewMachineEffects,
  GoalPreviewMachinePlan,
  GoalPreviewMachineContext,
  GoalPreviewStates,
  GoalPreviewMachineEffects,
  ReceiveUsersPreviewCount,
} from '../../previewUsers/hooks/usePreviewUsers.state';
import { countMeetsRequirement } from '../../previewUsers/previewUtils';

export const MutateGoalStates = {
  inactive: 'inactive',
  previewGoal: GoalPreviewStates.queryGoalPreview,
  previewInvalid: 'previewInvalid',
  previewError: GoalPreviewStates.error,
  confirmMutate: 'confirmMutate',
  mutateGoal: 'mutateGoal',
  mutationComplete: 'mutationComplete',
  mutationError: 'mutationError',
};

export interface MutateGoalMachineContext extends GoalPreviewMachineContext {
  goalVariables: unknown;
}

export const mutateGoalStateMachinePlan = {
  predictableActionArguments: true,
  id: 'mutateGoalStateMachine',
  initial: MutateGoalStates.inactive,
  context: {
    ...GoalPreviewMachinePlan.context,
    goalVariables: {},
  },
  states: {
    [MutateGoalStates.inactive]: {
      on: {
        [MutateGoalStates.previewGoal]: [
          {
            target: MutateGoalStates.previewGoal,
          },
        ],
      },
    },
    [MutateGoalStates.previewGoal]: {
      entry: 'storeGoalVars',
      exit: 'handlePreviewUsers',
      invoke: {
        src: 'queryPreviewUsers',
        onDone: [
          {
            target: MutateGoalStates.confirmMutate,
            cond: 'goalMeetsRequirement',
            actions: 'handlePreviewComplete',
          },
          {
            target: MutateGoalStates.previewInvalid,
            actions: 'handleInvalidPreview',
          },
        ],
        onError: {
          target: MutateGoalStates.previewError,
          actions: 'handleMutationError',
        },
      },
    },
    [MutateGoalStates.previewInvalid]: {
      on: {
        [MutateGoalStates.previewGoal]: {
          target: MutateGoalStates.previewGoal,
        },
      },
    },
    [MutateGoalStates.previewError]: {
      on: {
        [MutateGoalStates.previewGoal]: {
          target: MutateGoalStates.previewGoal,
        },
      },
    },
    [MutateGoalStates.confirmMutate]: {
      entry: 'storeGoalVars',
      always: [{ target: MutateGoalStates.mutateGoal, cond: 'shouldSelfTransitionToMutate' }],
      on: {
        [MutateGoalStates.previewGoal]: [
          {
            target: MutateGoalStates.previewGoal,
          },
        ],
        [MutateGoalStates.mutateGoal]: {
          target: MutateGoalStates.mutateGoal,
        },
      },
    },
    [MutateGoalStates.mutateGoal]: {
      invoke: {
        src: 'mutateGoal',
        onDone: {
          target: MutateGoalStates.mutationComplete,
          actions: 'handleMutationComplete',
        },
        onError: {
          target: MutateGoalStates.mutationError,
          actions: 'handleMutationError',
        },
      },
    },
    [MutateGoalStates.mutationComplete]: {
      on: {
        [MutateGoalStates.previewGoal]: {
          target: MutateGoalStates.previewGoal,
        },
      },
    },
    [MutateGoalStates.mutationError]: {
      on: {
        [MutateGoalStates.previewGoal]: [
          {
            target: MutateGoalStates.previewGoal,
          },
        ],
        [MutateGoalStates.mutateGoal]: {
          target: MutateGoalStates.mutateGoal,
        },
      },
    },
  },
};

export interface MutateGoalEvent<GoalVariables> extends AnyEventObject {
  payload: GoalVariables;
}
export interface GoalResponseEvent<GoalResponse> extends AnyEventObject {
  data: {
    data: GoalResponse;
  };
}
export interface GoalErrorEvent extends AnyEventObject {
  data: ApolloError;
}

export const mutateGoalStateMachine = createMachine<MutateGoalMachineContext>(
  mutateGoalStateMachinePlan,
);
export type MutateGoalStateMachineEffects<GoalResponse, GoalVariables> = {
  fetchGoalPreview: GoalPreviewMachineEffects['queryPreviewUsers'];
  mutateGoal: (
    options: MutationFunctionOptions<GoalResponse, GoalVariables>,
  ) => Promise<FetchResult<GoalResponse>>;
  onPreviewComplete?: () => void;
  onPreviewError?: () => void;
  onMutateComplete: (goal: GoalResponse) => void;
  onMutationError: (error: ApolloError) => void;
  shouldConfirmMutation: boolean;
};
export function createMutateStateMachineEffects<GoalResponse, GoalVariables>({
  fetchGoalPreview,
  mutateGoal,
  onMutateComplete,
  onMutationError,
  onPreviewComplete,
  onPreviewError,
  shouldConfirmMutation,
}: MutateGoalStateMachineEffects<GoalResponse, GoalVariables>) {
  const { actions: previewActions, services: previewServices } =
    createGoalPreviewMachineEffects<MutateGoalMachineContext>({
      queryPreviewUsers: fetchGoalPreview,
    });
  return {
    guards: {
      goalMeetsRequirement: (_: unknown, event: unknown) => {
        return countMeetsRequirement(
          (event as ReceiveUsersPreviewCount)?.data?.data?.estimatePredictiveGoalUserCount
            ?.total_count,
        );
      },
      shouldSelfTransitionToMutate: () => {
        // If criteria change
        if (shouldConfirmMutation) {
          return false;
        }
        return true;
      },
    },
    actions: {
      ...previewActions,
      handlePreviewComplete: () => {
        if (!onPreviewComplete) {
          return;
        }
        onPreviewComplete();
      },
      handleInvalidPreview: () => {
        if (!onPreviewError) {
          return;
        }
        onPreviewError();
      },
      handleMutationComplete: (_: unknown, event: unknown) => {
        onMutateComplete((event as GoalResponseEvent<GoalResponse>).data.data);
      },
      handleMutationError: (_: unknown, event: unknown) => {
        onMutationError((event as GoalErrorEvent).data);
      },
      storeGoalVars: assign((context: MutateGoalMachineContext, event: unknown) => {
        return {
          ...context,
          goalVariables: (event as MutateGoalEvent<GoalVariables>).payload || context.goalVariables,
        };
      }),
    },
    services: {
      queryPreviewUsers: (context: MutateGoalMachineContext, event: unknown) => {
        return previewServices.queryPreviewUsers(context, {
          ...(event as MutateGoalEvent<GoalVariables>),
          goal: (event as MutateGoalEvent<GoalVariables & { goal: unknown }>).payload.goal,
        });
      },
      mutateGoal: (context: MutateGoalMachineContext, event: unknown) =>
        pipe(
          // Pull goal data from either a direct event payload or stored from context
          {
            variables:
              (event as MutateGoalEvent<GoalVariables>).payload ||
              (context.goalVariables as GoalVariables),
          },
          mutateGoal,
          rejectGraphqlError,
        ),
    },
  };
}
